/**
 * DeleteAuditDialog.js
 *
 * Hard-delete confirmation modal. To protect against accidental clicks,
 * the user must TYPE the audit's exact name to enable the Delete button.
 *
 * Used by AppAdmin only.
 *
 * Props:
 *   open      : bool
 *   audit     : { id, name, ... } — the audit being deleted
 *   onClose   : () => void
 *   onDeleted : (deletedId) => void   — fired AFTER successful delete
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, Button, CircularProgress, Alert, Box, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { doc, deleteDoc } from 'firebase/firestore';
import { firestore as db } from './firebase.config';
import { useAuth } from '../auth/AuthContext';
import { applyWalletAction } from '../cbam/wallet/walletService';

const DeleteAuditDialog = ({ open, audit, onClose, onDeleted }) => {
  const [typedName, setTypedName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const { tenantId } = useAuth();

  // Reset typed name whenever the dialog opens for a new audit
  useEffect(() => {
    if (open) {
      setTypedName('');
      setError('');
    }
  }, [open, audit?.id]);

  const handleClose = () => {
    if (deleting) return; // block close mid-delete
    setTypedName('');
    setError('');
    onClose();
  };

  // The user must type the audit name EXACTLY (trimmed match) to enable delete
  const auditName = audit?.name || '';
  const canDelete = typedName.trim() === auditName.trim() && auditName.length > 0;

  const handleDelete = async () => {
    if (!canDelete || !audit?.id) return;
    setDeleting(true);
    setError('');

    try {
      // Hard delete — note: this removes the main audit doc, but any
      // sub-collection docs (form_a/main, form_b/main, etc.) stay orphaned.
      // Firestore doesn't cascade. For MR 1 that's acceptable; we can add
      // a cleanup batch later if storage becomes an issue.
      await deleteDoc(doc(db, 'auditReports', audit.id));

      if (tenantId) {
        try {
          await applyWalletAction(tenantId, 'cbam', 'delete', {
            description: 'Delete audit report',
            actor: 'user',
          });
        } catch (walletErr) {
          console.warn('Wallet deduction for delete failed:', walletErr?.message);
        }
      }

      onDeleted(audit.id);
    } catch (err) {
      console.error('Error deleting audit:', err);
      setError(
        'Could not delete audit. ' +
        (err?.message || 'Please try again.')
      );
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningAmberIcon />
        Delete Audit?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This will <strong>permanently delete</strong> the audit
          {auditName ? <> "<strong>{auditName}</strong>"</> : null} and all its
          form data. This action <strong>cannot be undone</strong>.
        </DialogContentText>

        <Alert severity="warning" sx={{ mb: 2 }}>
          To confirm, type the audit's name below exactly as shown.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Audit name:
          </Typography>
          <Box
            sx={{
              p: 1,
              backgroundColor: 'grey.100',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: 13,
              wordBreak: 'break-word',
            }}
          >
            {auditName || '(no name)'}
          </Box>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Type the audit name to confirm"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          disabled={deleting}
          error={typedName.length > 0 && !canDelete}
          helperText={
            typedName.length > 0 && !canDelete
              ? 'Name does not match yet.'
              : ' '
          }
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!canDelete || deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {deleting ? 'Deleting…' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAuditDialog;